<template>
    <ModalShell
        title="Parental Consent Agreement"
        subtitle="Please review and sign the parental consent form to add a minor to your account."
        width="sm:max-w-2xl sm:w-full w-11/12 max-h-[90vh]"
        body-class="gap-4"
        divider-class="mt-4"
        footer-class="pt-6"
    >
        <template #icon>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
        </template>
        <!-- Scrollable agreement text -->
        <!-- A readonly <textarea> stays focusable and is announced as a nameless edit field;
             a scrollable region keeps the same keyboard access without the form-control role. -->
        <div class="agreement-container">
            <div class="agreement-text" role="region" tabindex="0" aria-label="Parental Consent, Authorization, and Limited Agency Agreement">{{ agreementText }}</div>
        </div>

        <!-- Checkbox -->
        <div class="consent-checkbox">
            <PrimeCheckbox
                v-model="agreed"
                :binary="true"
                inputId="consent-agree"
            />
            <label for="consent-agree" class="checkbox-label">
                I have read and agree to the Parental Consent, Authorization, and Limited Agency Agreement.
            </label>
        </div>

        <!-- Disclosure text -->
        <div class="disclosure-text">
            <p>By checking the box above and typing my name below, I certify that I am the parent or legal guardian of the minor child being added to this account. I grant Privacy Bee LLC express consent to collect, store, and process this minor's personal information solely to provide privacy protection services, and I appoint Privacy Bee LLC as my authorized agent to submit data deletion, suppression, and opt-out requests on their behalf under applicable federal and state privacy laws. I represent that no court order or custody restriction limits my authority to provide this consent. I acknowledge that my typed name constitutes a legally binding electronic signature with the same force and effect as a handwritten signature under the Electronic Signatures in Global and National Commerce Act (15 U.S.C. &sect; 7001 et seq.).</p>
        </div>

        <!-- Full Legal Name input -->
        <ModalField class="w-full" label="Full Legal Name" required input-id="minor-consent-signer-name">
            <PrimeInputText
                id="minor-consent-signer-name"
                v-model="signerName"
                class="w-full"
                placeholder="Enter your full legal name"
                aria-required="true"
                @keydown.enter="onSubmit"
            />
        </ModalField>
        <template #footer>
            <PrimeButton
                label="Cancel"
                class="p-btn-root_secondary"
                @click="closeDialog"
            />
            <PrimeButton
                label="Sign & Continue"
                variant="primary"
                :disabled="!canSubmit"
                :loading="isSubmitting"
                @click="onSubmit"
            />
        </template>
    </ModalShell>
</template>

<script setup lang="ts">
import PrimeInputText from 'primevue/inputtext';
import PrimeCheckbox from 'primevue/checkbox';
import ModalField from '~/components/modals/shared/ModalField.vue';
import ModalShell from '~/components/modals/shared/ModalShell.vue';
import { useContactsStore } from '~/stores/person/contacts';
import { ModalNames } from '~/types/modals';
import { getMinorConsentAgreementText } from './minor-consent-agreement-variant';

const contactsStore = useContactsStore();
const dialogRef = getDialogRef(ModalNames.ADD_CHILD_CONSENT);
const agreementText = computed(() => getMinorConsentAgreementText(contactsStore.minorConsentVariant));

const agreed = ref(false);
const signerName = ref('');
const isSubmitting = ref(false);

const canSubmit = computed(() => {
    return agreed.value && signerName.value.trim().length >= 2 && !isSubmitting.value;
});

const closeDialog = () => {
    dialogRef.value.close();
};

const onSubmit = async () => {
    if (!canSubmit.value) return;

    isSubmitting.value = true;
    const success = await contactsStore.signMinorConsent(signerName.value.trim());
    isSubmitting.value = false;

    if (success) {
        closeDialog();
        useDynamicDialog(ModalNames.ADD_CHILD_FORM);
    }
};
</script>

<style lang="scss" scoped>
.agreement-container {
    @apply w-full border border-zinc-200 rounded-lg overflow-hidden;
}

.agreement-text {
    @apply w-full p-4 h-64 overflow-y-auto bg-transparent;
    @apply text-xs text-zinc-600 leading-relaxed font-mono;
    cursor: default;
    white-space: pre-wrap;
}

.consent-checkbox {
    @apply flex items-start gap-3 w-full;
}

.checkbox-label {
    @apply text-sm text-zinc-700 leading-5 cursor-pointer;
}

.disclosure-text {
    @apply w-full;

    p {
        @apply text-xs text-zinc-500 leading-relaxed;
    }
}

</style>
